import { useCallback, useEffect, useRef, useState } from 'react'
import { AlertCircle, CheckCircle, Clock, Eye, FlaskConical, ListChecks, Link2, MapPin, PauseCircle, RefreshCw, RotateCcw, Send, Users, XCircle } from 'lucide-react'
import { supabase } from '../../lib/supabase'
import { callEdgeFunction } from '../../../lib/edgeFunctions'
import { jourCourt } from '../../../lib/communication/preSession'
import {
  actionPrincipale,
  erreurEnClair,
  libelleSituationCommunaute,
  nomDeLaCommunaute,
  normaliserLienWhatsApp,
  nouvellesFamilles,
  parSemaineDuCalendrier,
  renvoiDuLien,
  resumePlanification,
  resumeRenvoi,
  situationCommunaute,
  suiteDuLot,
  type EtatCommunautes,
  type Planification,
  type Renvoi,
  type ReponseLot,
  type SemaineDuCentre,
} from '../../../lib/communication/communauteWhatsapp'
import { ApercuInvitationWhatsapp } from './ApercuInvitationWhatsapp'
import { ListeDeControleWhatsapp } from './ListeDeControleWhatsapp'

/**
 * L'onglet « Communautés WhatsApp » (17 octobre 2026).
 *
 * Une communauté par centre et par semaine. L'administration colle le lien de
 * la communauté ; quelques jours avant la semaine, un clic invite chaque
 * famille attendue, avec le groupe (le stage) de chacun de ses enfants. Les
 * familles inscrites ensuite reçoivent le lien dans leur courriel
 * pré-session. La liste de contrôle donne à la référente les parents attendus
 * par groupe.
 *
 * Le clic planifie (`planifier_invitations_whatsapp`) ; l'écran appelle
 * ensuite `envoyer-invitations-whatsapp` lot après lot, une minute d'écart,
 * tant qu'il en reste. Fermer l'onglet interrompt l'envoi sans rien perdre :
 * les invitations restantes attendent le clic suivant.
 *
 * Le mode test (allumé à l'installation) : aucune famille n'est écrite. Le
 * bouton devient « Essai » : chaque courriel part à l'admin qui clique, objet
 * « [TEST] », par les mêmes lots ; la base refuse alors l'envoi réel.
 *
 * Le lien corrigé (19 octobre 2026) : quand le lien est remplacé après
 * l'envoi, la carte compte les familles qui ont reçu l'ancien, et « Renvoyer le
 * nouveau lien » leur écrit à nouveau, objet « Lien corrigé – … » et bandeau
 * en tête, par les mêmes lots (`renvoyer_lien_whatsapp`).
 */

interface EnvoiEnCours {
  cle: string
  essai: boolean
  envoyes: number
  restantes: number | null
  attente: number
}

function cleDe(s: SemaineDuCentre): string {
  return `${s.center_id}|${s.semaine_du}`
}

function dateHeure(iso: string): string {
  return new Date(iso).toLocaleString('fr-BE', {
    timeZone: 'Europe/Brussels', weekday: 'short', day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit',
  })
}

const attendre = (ms: number) => new Promise(r => setTimeout(r, ms))

export function CommunautesWhatsapp() {
  const [etat, setEtat] = useState<EtatCommunautes | null>(null)
  const [chargement, setChargement] = useState(true)
  const [erreur, setErreur] = useState<string | null>(null)
  const [message, setMessage] = useState<string | null>(null)
  const [action, setAction] = useState<string | null>(null)
  const [brouillons, setBrouillons] = useState<Record<string, string>>({})
  const [envoi, setEnvoi] = useState<EnvoiEnCours | null>(null)
  const [apercu, setApercu] = useState<SemaineDuCentre | null>(null)
  const [liste, setListe] = useState<SemaineDuCentre | null>(null)
  const [referente, setReferente] = useState<{ nom: string; telephone: string } | null>(null)
  const arret = useRef(false)

  const charger = useCallback(async () => {
    setChargement(true)
    const { data, error } = await supabase.rpc('etat_communautes_whatsapp', { p_jours: 21 })
    if (error) {
      setErreur(erreurEnClair(error))
      setEtat(null)
    } else {
      setErreur(null)
      setEtat(data as EtatCommunautes)
    }
    setChargement(false)
  }, [])

  useEffect(() => {
    charger()
    // Quitter l'onglet arrête la boucle des lots ; le reste attend le clic suivant.
    return () => {
      arret.current = true
    }
  }, [charger])

  const enregistrerLien = async (s: SemaineDuCentre) => {
    const cle = cleDe(s)
    const lien = normaliserLienWhatsApp(brouillons[cle] ?? '')
    if (!lien) {
      setErreur("Ce n'est pas un lien d'invitation WhatsApp : il commence par https://chat.whatsapp.com/")
      return
    }
    const dejaInvitees = s.communaute?.envoyees ?? 0
    if (s.communaute && dejaInvitees > 0 && lien !== s.communaute.lien
        && !window.confirm(`${dejaInvitees} famille(s) ont déjà reçu l'ancien lien. Le remplacer ? Vous pourrez ensuite leur renvoyer le nouveau avec le bouton « Renvoyer le nouveau lien ».`)) {
      return
    }
    setAction(cle)
    setErreur(null)
    setMessage(null)
    const { error } = await supabase.rpc('enregistrer_communaute_whatsapp', {
      p_center_id: s.center_id,
      p_semaine_du: s.semaine_du,
      p_lien: lien,
    })
    if (error) {
      setErreur(erreurEnClair(error))
    } else {
      setMessage(`Lien enregistré pour ${nomDeLaCommunaute(s)}.`)
      setBrouillons(b => {
        const reste = { ...b }
        delete reste[cle]
        return reste
      })
      await charger()
    }
    setAction(null)
  }

  /*
    Les lots, l'un après l'autre. `essai` : le mode test, qui avance par
    décalage (aucune invitation n'est prise) et envoie tout à l'admin.
  */
  const envoyerLesLots = async (s: SemaineDuCentre, communauteId: string, essai: boolean, renvoi = false) => {
    const cle = cleDe(s)
    arret.current = false
    let envoyes = 0
    let restantes: number | null = null
    let decalage = 0
    let envoyeA: string | null = null
    for (let tour = 0; tour < 500; tour++) {
      setEnvoi({ cle, essai, envoyes, restantes, attente: 0 })
      let r: ReponseLot
      try {
        r = await callEdgeFunction<ReponseLot>('envoyer-invitations-whatsapp', essai
          ? { communaute_id: communauteId, mode: 'essai', decalage }
          : { communaute_id: communauteId, mode: 'lot' })
      } catch (e) {
        setErreur(e instanceof Error ? e.message : 'Envoi interrompu')
        break
      }
      envoyes += r.envoyes ?? 0
      restantes = r.restantes ?? restantes
      envoyeA = r.envoye_a ?? envoyeA
      if (essai && r.raison === 'ok') decalage = r.suivant ?? decalage
      const suite = suiteDuLot(r)
      if (suite.message) setErreur(suite.message)
      if (!suite.continuer || arret.current) break
      for (let t = suite.attendreSecondes; t > 0 && !arret.current; t--) {
        setEnvoi({ cle, essai, envoyes, restantes, attente: t })
        await attendre(1000)
      }
      if (arret.current) break
    }
    setEnvoi(null)
    setMessage(essai
      ? `Essai ${nomDeLaCommunaute(s)} : ${envoyes} courriel${envoyes > 1 ? 's' : ''} « [TEST] » envoyé${envoyes > 1 ? 's' : ''}${envoyeA ? ` à ${envoyeA}` : ''}. Aucune famille n'a rien reçu.`
      : renvoi
        ? `${nomDeLaCommunaute(s)} : ${envoyes} courriel${envoyes > 1 ? 's' : ''} « Lien corrigé » envoyé${envoyes > 1 ? 's' : ''}.`
        : `${nomDeLaCommunaute(s)} : ${envoyes} invitation${envoyes > 1 ? 's' : ''} envoyée${envoyes > 1 ? 's' : ''}.`)
    await charger()
  }

  const essayer = async (s: SemaineDuCentre, n: number) => {
    if (!s.communaute) return
    if (!window.confirm(`Mode test : ${n} courriel(s) « [TEST] » partiront à VOTRE adresse, un par famille de ${nomDeLaCommunaute(s)}. Aucune famille ne recevra rien. Continuer ?`)) return
    setAction(cleDe(s))
    setErreur(null)
    setMessage(null)
    await envoyerLesLots(s, s.communaute.id, true)
    setAction(null)
  }

  const envoyer = async (s: SemaineDuCentre, relancerEchecs: boolean) => {
    if (!s.communaute) return
    const n = relancerEchecs ? s.communaute.echecs : nouvellesFamilles(s) + s.communaute.en_attente
    const question = relancerEchecs
      ? `Relancer les ${n} adresse(s) refusée(s) par Gmail pour ${nomDeLaCommunaute(s)} ?`
      : `Inviter ${n} famille(s) à la communauté ${nomDeLaCommunaute(s)} ? Chaque famille reçoit un seul courriel, avec le groupe de chacun de ses enfants.`
    if (!window.confirm(question)) return
    const cle = cleDe(s)
    setAction(cle)
    setErreur(null)
    setMessage(null)
    const { data, error } = await supabase.rpc('planifier_invitations_whatsapp', {
      p_communaute_id: s.communaute.id,
      p_relancer_echecs: relancerEchecs,
    })
    if (error) {
      setErreur(erreurEnClair(error))
      setAction(null)
      return
    }
    const plan = data as Planification
    setMessage(resumePlanification(plan))
    if (plan.a_envoyer > 0) await envoyerLesLots(s, s.communaute.id, false)
    else await charger()
    setAction(null)
  }

  const renvoyer = async (s: SemaineDuCentre, n: number) => {
    if (!s.communaute) return
    if (!window.confirm(`Renvoyer le nouveau lien aux ${n} famille(s) de ${nomDeLaCommunaute(s)} qui ont reçu l'ancien ? Le courriel s'ouvre sur « Le lien envoyé le … ne fonctionnait pas : voici le bon. »`)) return
    const cle = cleDe(s)
    setAction(cle)
    setErreur(null)
    setMessage(null)
    const { data, error } = await supabase.rpc('renvoyer_lien_whatsapp', { p_communaute_id: s.communaute.id })
    if (error) {
      setErreur(erreurEnClair(error, "Le renvoi du lien n'est pas encore installé sur cette base (migration 20261019100746_le_lien_corrige_whatsapp)."))
      setAction(null)
      return
    }
    const renvoi = data as Renvoi
    setMessage(resumeRenvoi(renvoi))
    if (renvoi.a_envoyer > 0) await envoyerLesLots(s, s.communaute.id, false, renvoi.renvoyees > 0)
    else await charger()
    setAction(null)
  }

  const regler = async (params: { p_referente_nom?: string; p_referente_telephone?: string; p_lever_la_pause?: boolean; p_mode_test?: boolean }) => {
    setAction('reglage')
    setErreur(null)
    const { error } = await supabase.rpc('regler_communautes_whatsapp', params)
    if (error) {
      setErreur(error.code === '23514' ? 'Numéro de téléphone illisible (chiffres, espaces, points ; « + » possible).' : erreurEnClair(error))
    } else {
      setReferente(null)
      await charger()
    }
    setAction(null)
  }

  if (chargement && !etat) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
      </div>
    )
  }

  if (!etat) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-sm text-red-800 flex items-start">
        <AlertCircle className="h-5 w-5 mr-2 flex-shrink-0" />
        <span>{erreur ?? 'État des communautés illisible.'}</span>
      </div>
    )
  }

  const r = etat.reglages
  const calendrier = parSemaineDuCalendrier(etat.semaines)

  const basculerModeTest = () => {
    const question = r.mode_test
      ? 'Éteindre le mode test : les prochains clics « Envoyer » écriront aux familles. Continuer ?'
      : 'Rallumer le mode test : plus aucune invitation ne partira aux familles, les essais partiront à votre adresse. Continuer ?'
    if (window.confirm(question)) regler({ p_mode_test: !r.mode_test })
  }

  return (
    <div className="space-y-6">
      {apercu && <ApercuInvitationWhatsapp semaine={apercu} onClose={() => setApercu(null)} />}
      {liste && <ListeDeControleWhatsapp semaine={liste} onClose={() => setListe(null)} />}

      {/* Le principe, la référente, la pause */}
      <div className="bg-white border border-gray-200 rounded-lg p-5 space-y-4">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Inviter les parents à la communauté WhatsApp</h3>
            <p className="text-sm text-gray-600 mt-1">
              Une communauté par centre et par semaine, un groupe par stage. Collez le lien de la communauté ; à partir de{' '}
              {r.jours_avant} jours avant la semaine, un clic invite chaque famille attendue (inscriptions payées ou en attente
              de paiement), avec le groupe de chacun de ses enfants. Les familles inscrites ensuite reçoivent le lien dans leur
              courriel pré-session.
            </p>
            <p className="text-xs text-gray-500 mt-1">
              Un seul courriel par famille et par communauté : recliquer n'écrit qu'aux familles qui n'ont rien reçu.
              {' '}{etat.envoyes_24h} invitation{etat.envoyes_24h > 1 ? 's' : ''} sur 24 h (plafond {r.plafond_quotidien}).
            </p>
          </div>
          <button onClick={charger} className="flex items-center space-x-1 text-sm text-gray-600 hover:text-gray-900" title="Actualiser">
            <RefreshCw className={`h-4 w-4 ${chargement ? 'animate-spin' : ''}`} />
            <span>Actualiser</span>
          </button>
        </div>

        {r.mode_test ? (
          <div className="bg-amber-50 border-2 border-amber-300 rounded-lg p-3 text-sm text-amber-900 flex flex-wrap items-center gap-3">
            <FlaskConical className="h-5 w-5 flex-shrink-0" />
            <span className="flex-1 min-w-[16rem]">
              <strong>Mode test allumé : aucune famille ne reçoit rien.</strong> Le bouton « Essai » de chaque carte, actif une
              fois le lien de la communauté enregistré, envoie chaque courriel à votre adresse, objet « [TEST] », un par famille,
              avec l'adresse où il serait parti. La fenêtre des {r.jours_avant} jours ne s'applique pas aux essais.
            </span>
            <button
              onClick={basculerModeTest}
              disabled={action !== null || envoi !== null}
              className="px-3 py-1 rounded-lg border border-amber-400 bg-white hover:bg-amber-100 disabled:opacity-50"
            >
              Éteindre le mode test
            </button>
          </div>
        ) : (
          <div className="flex flex-wrap items-center gap-3 text-sm">
            <span className="px-3 py-1 rounded-full font-medium bg-green-100 text-green-800">Envois réels : les invitations partent aux familles</span>
            <button
              onClick={basculerModeTest}
              disabled={action !== null || envoi !== null}
              className="px-3 py-1 rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-50"
            >
              Rallumer le mode test
            </button>
          </div>
        )}

        <div className="flex flex-wrap items-center gap-3 text-sm">
          <span className="text-gray-700">Référente citée dans le courriel :</span>
          {referente ? (
            <>
              <input
                value={referente.nom}
                onChange={e => setReferente({ ...referente, nom: e.target.value })}
                className="border border-gray-300 rounded px-2 py-1 w-32"
                aria-label="Nom de la référente"
              />
              <input
                value={referente.telephone}
                onChange={e => setReferente({ ...referente, telephone: e.target.value })}
                className="border border-gray-300 rounded px-2 py-1 w-40"
                aria-label="Téléphone de la référente"
              />
              <button
                onClick={() => regler({ p_referente_nom: referente.nom, p_referente_telephone: referente.telephone })}
                disabled={action !== null}
                className="px-3 py-1 rounded-lg bg-green-600 text-white hover:bg-green-700 disabled:opacity-50"
              >
                Enregistrer
              </button>
              <button onClick={() => setReferente(null)} className="px-3 py-1 rounded-lg border border-gray-300 hover:bg-gray-50">
                Annuler
              </button>
            </>
          ) : (
            <>
              <span className="font-medium text-gray-900">{r.referente_nom} — {r.referente_telephone}</span>
              <button
                onClick={() => setReferente({ nom: r.referente_nom, telephone: r.referente_telephone })}
                className="px-3 py-1 rounded-lg border border-gray-300 hover:bg-gray-50"
              >
                Modifier
              </button>
            </>
          )}
        </div>

        {r.pause_jusqu_a && (
          <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 text-sm text-orange-900 flex flex-wrap items-center gap-3">
            <PauseCircle className="h-5 w-5" />
            <span>
              Gmail a freiné les envois : reprise le {dateHeure(r.pause_jusqu_a)}.
              {r.pause_motif && <span className="block text-xs text-orange-700 mt-1">{r.pause_motif}</span>}
            </span>
            <button
              onClick={() => regler({ p_lever_la_pause: true })}
              disabled={action !== null}
              className="ml-auto px-3 py-1 text-sm rounded-lg border border-orange-300 hover:bg-orange-100 disabled:opacity-50"
            >
              Lever la pause
            </button>
          </div>
        )}
        {message && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-sm text-green-800 flex items-start">
            <CheckCircle className="h-5 w-5 mr-2 flex-shrink-0" />
            <span>{message}</span>
          </div>
        )}
        {erreur && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-800 flex items-start">
            <AlertCircle className="h-5 w-5 mr-2 flex-shrink-0" />
            <span>{erreur}</span>
          </div>
        )}
      </div>

      {calendrier.length === 0 && (
        <div className="text-center py-12 text-gray-500">
          <Users className="mx-auto h-12 w-12 text-gray-400 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Aucune semaine à venir</h3>
          <p>Aucune session ne commence d'ici trois semaines.</p>
        </div>
      )}

      {/* Une section par semaine, une carte par centre */}
      {calendrier.map(sem => (
        <section key={sem.semaine_du} className="space-y-3">
          <h3 className="text-lg font-semibold text-gray-900">Semaine du {jourCourt(sem.semaine_du)}</h3>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {sem.centres.map(s => {
              const cle = cleDe(s)
              const c = s.communaute
              const sit = situationCommunaute(s, etat.aujourd_hui)
              const brouillon = brouillons[cle] ?? c?.lien ?? ''
              const lienValide = normaliserLienWhatsApp(brouillon)
              const lienModifie = lienValide !== null && lienValide !== c?.lien
              const enEnvoi = envoi?.cle === cle
              const occupe = action !== null || envoi !== null
              const couleur =
                sit === 'servie' ? 'border-green-200 bg-green-50'
                  : sit === 'sans_lien' || sit === 'echecs' || sit === 'ancien_lien' ? 'border-orange-200 bg-orange-50'
                    : sit === 'a_envoyer' || sit === 'nouvelles_familles' || sit === 'en_cours' ? 'border-blue-200 bg-blue-50'
                      : 'border-gray-200 bg-white'
              const principale = actionPrincipale(s, etat.aujourd_hui, r.mode_test)
              const correction = renvoiDuLien(s, etat.aujourd_hui, r.mode_test)
              return (
                <div key={cle} className={`border rounded-lg p-4 ${couleur}`}>
                  <div className="flex items-start justify-between gap-2">
                    <h4 className="font-semibold text-gray-900 flex items-center">
                      <MapPin className="h-4 w-4 mr-2" />
                      {s.centre}
                    </h4>
                    <span className="text-xs text-gray-500">du {jourCourt(s.debut)} au {jourCourt(s.fin)}</span>
                  </div>
                  <p className="mt-1 text-sm text-gray-700">
                    Nom de la communauté : <span className="font-medium">{nomDeLaCommunaute(s)}</span>
                  </p>
                  <p className="text-sm text-gray-700">Groupes : {s.groupes.join(', ') || '—'}</p>
                  <div className="mt-1 flex items-center text-sm text-gray-700">
                    <Users className="h-4 w-4 mr-2 text-gray-500" />
                    {s.familles_attendues} famille{s.familles_attendues > 1 ? 's' : ''} attendue{s.familles_attendues > 1 ? 's' : ''}
                    {s.familles_sans_adresse > 0 && (
                      <span className="ml-1 text-orange-700">(+ {s.familles_sans_adresse} sans adresse email)</span>
                    )}
                  </div>

                  <div className="mt-3 flex flex-wrap items-center gap-2">
                    <Link2 className="h-4 w-4 text-gray-500" />
                    <input
                      value={brouillon}
                      onChange={e => setBrouillons(b => ({ ...b, [cle]: e.target.value }))}
                      placeholder="https://chat.whatsapp.com/…"
                      className={`flex-1 min-w-[12rem] border rounded px-2 py-1 text-sm ${brouillon && !lienValide ? 'border-red-300' : 'border-gray-300'}`}
                      aria-label={`Lien de la communauté ${nomDeLaCommunaute(s)}`}
                      disabled={sit === 'terminee'}
                    />
                    <button
                      onClick={() => enregistrerLien(s)}
                      disabled={occupe || !lienModifie}
                      className="px-3 py-1 text-xs rounded bg-gray-800 text-white hover:bg-gray-900 disabled:opacity-40"
                    >
                      {c ? 'Remplacer' : 'Enregistrer'}
                    </button>
                  </div>

                  <div className="mt-3 text-sm font-medium text-gray-900">{libelleSituationCommunaute(s, etat.aujourd_hui)}</div>
                  {c?.invitations_lancees_le && (
                    <div className="text-xs text-gray-600">Invitations lancées le {dateHeure(c.invitations_lancees_le)}</div>
                  )}
                  {c && (c.envoyees > 0 || c.en_attente > 0 || c.echecs > 0) && (
                    <div className="mt-2 flex flex-wrap gap-3 text-xs">
                      <span className="flex items-center text-green-700">
                        <CheckCircle className="h-3 w-3 mr-1" />{c.envoyees} invitée{c.envoyees > 1 ? 's' : ''}
                        {c.par_pre_session > 0 && <> (dont {c.par_pre_session} par le courriel pré-session)</>}
                        {(c.corrigees ?? 0) > 0 && <> (dont {c.corrigees} avec le lien corrigé)</>}
                      </span>
                      {(c.ancien_lien ?? 0) > 0 && <span className="flex items-center text-orange-700"><AlertCircle className="h-3 w-3 mr-1" />{c.ancien_lien} avec un ancien lien</span>}
                      {c.en_attente > 0 && <span className="flex items-center text-blue-700"><Clock className="h-3 w-3 mr-1" />{c.en_attente} en attente</span>}
                      {c.echecs > 0 && <span className="flex items-center text-red-700"><XCircle className="h-3 w-3 mr-1" />{c.echecs} échec{c.echecs > 1 ? 's' : ''}</span>}
                    </div>
                  )}
                  {c?.derniere_erreur && <p className="mt-1 text-xs text-red-700 break-words">Dernière erreur : {c.derniere_erreur}</p>}

                  {enEnvoi && envoi && (
                    <div className="mt-3 flex flex-wrap items-center gap-3 text-sm text-blue-900 bg-white border border-blue-200 rounded p-2">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                      <span>
                        {envoi.essai && <strong>Essai : </strong>}
                        {envoi.envoyes} envoyée{envoi.envoyes > 1 ? 's' : ''}{envoi.essai && ' à votre adresse'}
                        {envoi.restantes !== null && <> · {envoi.restantes} restante{envoi.restantes > 1 ? 's' : ''}</>}
                        {envoi.attente > 0 && <> · lot suivant dans {envoi.attente} s</>}
                      </span>
                      <button
                        onClick={() => { arret.current = true }}
                        className="ml-auto px-2 py-1 text-xs rounded border border-blue-300 hover:bg-blue-50"
                      >
                        Arrêter après ce lot
                      </button>
                    </div>
                  )}

                  <div className="mt-3 flex flex-wrap gap-2">
                    {c && s.familles_attendues > 0 && (
                      <button
                        onClick={() => setApercu(s)}
                        className="flex items-center px-3 py-1 text-xs rounded border border-green-300 text-green-800 bg-white hover:bg-green-50"
                        title="Voir le courriel d'une famille et vous l'envoyer, sans écrire à personne"
                      >
                        <Eye className="h-3 w-3 mr-1" />
                        Aperçu / test
                      </button>
                    )}
                    {principale?.geste === 'essai' && (
                      <button
                        onClick={() => essayer(s, principale.n)}
                        disabled={occupe || principale.bloquee !== undefined}
                        className="flex items-center px-3 py-1 text-xs rounded bg-amber-500 text-white hover:bg-amber-600 disabled:opacity-50 disabled:cursor-not-allowed"
                        title={principale.bloquee ?? 'Mode test : chaque courriel part à votre adresse, aucune famille ne reçoit rien'}
                      >
                        <FlaskConical className="h-3 w-3 mr-1" />
                        {principale.libelle}
                      </button>
                    )}
                    {principale?.geste === 'envoyer' && (
                      <button
                        onClick={() => envoyer(s, false)}
                        disabled={occupe}
                        className="flex items-center px-3 py-1 text-xs rounded bg-green-600 text-white hover:bg-green-700 disabled:opacity-50"
                      >
                        <Send className="h-3 w-3 mr-1" />
                        {principale.libelle}
                      </button>
                    )}
                    {correction && (
                      <button
                        onClick={() => renvoyer(s, correction.n)}
                        disabled={occupe || correction.bloquee !== undefined}
                        className="flex items-center px-3 py-1 text-xs rounded bg-orange-600 text-white hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed"
                        title={correction.bloquee ?? "Un courriel « Lien corrigé » à chaque famille qui a reçu l'ancien lien"}
                      >
                        <RotateCcw className="h-3 w-3 mr-1" />
                        {correction.libelle}
                      </button>
                    )}
                    {!r.mode_test && c && c.echecs > 0 && sit !== 'terminee' && sit !== 'trop_tot' && (
                      <button
                        onClick={() => envoyer(s, true)}
                        disabled={occupe}
                        className="flex items-center px-3 py-1 text-xs rounded border border-orange-300 text-orange-800 bg-white hover:bg-orange-50 disabled:opacity-50"
                      >
                        <RotateCcw className="h-3 w-3 mr-1" />
                        Relancer {c.echecs} échec{c.echecs > 1 ? 's' : ''}
                      </button>
                    )}
                    {(s.familles_attendues > 0 || s.familles_sans_adresse > 0) && (
                      <button
                        onClick={() => setListe(s)}
                        className="flex items-center px-3 py-1 text-xs rounded border border-gray-300 text-gray-700 bg-white hover:bg-gray-50"
                        title="Les parents attendus par groupe, pour accepter les demandes d'accès"
                      >
                        <ListChecks className="h-3 w-3 mr-1" />
                        Liste de contrôle
                      </button>
                    )}
                  </div>
                  {principale?.bloquee && <p className="mt-2 text-xs text-gray-600">{principale.bloquee}</p>}
                  {correction?.bloquee && <p className="mt-2 text-xs text-gray-600">{correction.bloquee}</p>}
                </div>
              )
            })}
          </div>
        </section>
      ))}
    </div>
  )
}
